import I18n from 'i18n-js';
import React from 'react';
import BankAccountInformation from './bank_account_information';
import {
  PendingPlaidView,
} from './shared/plaid_helpers';
import { RCText, RCTextWithIcon, RCFlex } from '@reverbdotcom/cadence/components';
import { LockIcon } from '@reverbdotcom/cadence/icons/react';
import { Paths } from './shared/url_helpers';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';

function StoredSecurelyText() {
  return (
    <div>
      <RCTextWithIcon svgComponent={LockIcon} placement="left">
        <RCText size="350" weight="bold" htmlTag="span" color="rc-color-text-secondary">
          {I18n.t('discovery.payoutMethod.directCheckoutProfile.storedSecurelyAndEncrypted')}
        </RCText>
      </RCTextWithIcon>
    </div>
  );
}

const PayoutBankAccountForm = ({
  accountRepEmail = null,
  accountRepName = null,
  bankChangesLocked = false,
  countryCodeOptions = null,
  dcProfileComplete,
  formPrefix,
  hideBankAccountEditForm,
  isGetPaid = false,
  isPlaidDcp,
  isPlaidEnabled,
  onCountryChange,
  onCurrencyChange,
  pendingPlaidAccountId,
  plaidAccountStatus,
  profileAttributes,
  selectedPayoutCurrency,
  shopCurrency,
  showCollapsedAchSummary,
  showPlaidSummary,
  summaryConfig,
  toggleEditing,
  shopCountryCode,
  payoutCurrencies = null,
  selectedWalletEligiblePayoutMethod = null,
  shouldShowWalletPayouts = false,
  buttonRef = null,
}) => {
  const nameForField = React.useCallback(
    (fieldName) => (
      `${formPrefix}[${fieldName}]`
    ),
    [formPrefix],
  );
  const pendingAutomaticVerification = plaidAccountStatus?.includes('pending_automatic');
  const pendingManualVerification = plaidAccountStatus?.includes('pending_manual');

  return (
    <>
      {isPlaidEnabled && (pendingAutomaticVerification || pendingManualVerification) && (
        <PendingPlaidView plaidAccountStatus={plaidAccountStatus} />
      )}

      {hideBankAccountEditForm && dcProfileComplete && !showCollapsedAchSummary && (
        <p className="mb-2">
          {/* disabling inline here so we can enable this rule globally, but this should be fixed the next time this file is touched */}
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <a data-nevermind-link onClick={toggleEditing}>
            {I18n.t('discovery.payoutMethod.directCheckoutProfile.nevermind')}
          </a>
        </p>
      )}

      {shouldShowWalletPayouts && (
        <RCFlex justify="center" direction="col" align="center">
          <RCText.Utility size="450" weight="semibold">
            {profileAttributes.maskedNameSummary}
          </RCText.Utility>

          {dcProfileComplete && (
            <div className="mb-2">
              <StoredSecurelyText />
            </div>
          )}
        </RCFlex>
      )}

      {!shouldShowWalletPayouts && (
        <p className="strong mb-0">
          {I18n.t('discovery.payoutMethod.directCheckoutProfile.bankInfo')}
          <span className="ml-space size-90 opacity-60">
            <span className="ph ph-lock mr-space" />
            {I18n.t('discovery.payoutMethod.directCheckoutProfile.storedSecurelyAndEncrypted')}
          </span>
        </p>
      )}

      {!shouldShowWalletPayouts && (showCollapsedAchSummary || showPlaidSummary) && (
        <div className="mt-0 mb-2" data-readonly-profile-fields>
          <div>
            <p className="mb-0">{profileAttributes.nameOnAccount}</p>
            <p className="mb-0">{profileAttributes.maskedNameSummary}</p>
            {profileAttributes.bankName && (
              <p className="mb-0">{profileAttributes.bankName}</p>
            )}
          </div>
        </div>
      )}

      {!(isPlaidEnabled && pendingAutomaticVerification) && (
        <>
          <BankAccountInformation
            accountRepEmail={accountRepEmail || 'sellerengagement@reverb.com'}
            accountRepName={accountRepName || 'Reverb'}
            bankChangesLocked={bankChangesLocked}
            countryCodeOptions={countryCodeOptions}
            formPrefix={formPrefix}
            isGetPaid={isGetPaid}
            isPlaidDcp={isPlaidDcp}
            isPlaidEnabled={isPlaidEnabled}
            nameForField={nameForField}
            onCountryChange={onCountryChange}
            onCurrencyChange={onCurrencyChange}
            payoutCurrencies={payoutCurrencies}
            pendingPlaidAccountId={pendingManualVerification ? pendingPlaidAccountId : null}
            profileAttributes={profileAttributes}
            selectedPayoutCurrency={selectedPayoutCurrency}
            shopCountryCode={shopCountryCode}
            shopCurrency={shopCurrency}
            showCollapsedAchSummary={showCollapsedAchSummary}
            showPlaidSummary={showPlaidSummary}
            summaryConfig={summaryConfig}
            toggleEditing={toggleEditing}
            selectedWalletEligiblePayoutMethod={selectedWalletEligiblePayoutMethod}
            shouldShowWalletPayouts={shouldShowWalletPayouts}
            buttonRef={buttonRef}
          />

          {shouldShowWalletPayouts && (
            <>
              <RCFlex direction="col" align="center">
                {dcProfileComplete && (
                  <CoreLink
                    to={Paths.helpCenterArticlesUrl.expand({ articleId: 360002778613 })}
                    className="text-color-primary text-link"
                    target="_blank"
                  >
                    <RCText.Fine>
                      {I18n.t('discovery.payoutMethod.directCheckoutProfile.learnMore')}
                    </RCText.Fine>
                  </CoreLink>
                )}
              </RCFlex>
            </>
          )}
        </>
      )}
    </>
  );
};

export default PayoutBankAccountForm;
